/**
 * <rlhf-annotated-text> — renders letter text with existing annotations as inline
 * marks + a gutter of chips, and turns a text selection into a new annotation.
 *
 * The operator selects a span, presses "Markierung annotieren", and a small form
 * (kind / severity / gate pickers + comment + optional rewrite) captures the
 * signal; on submit the element computes a W3C TextQuoteSelector from the
 * selection and appends an `annotation.Added` event into the injected SessionStore.
 * The element never persists — it only emits (the host's store does the rest).
 *
 * Data arrives via PROPERTIES (payloads, not identity): `text`, `subjectRef`,
 * `gates`, `actor`. Existing marks are read LIVE from the store's folded state,
 * filtered to this element's `subjectRef`.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, styleMap, type TemplateResult } from "@platform/web-kit/render"
import {
  ANNOTATION_KINDS,
  SEVERITIES,
  type AnnotationKind,
  type AnnotationOf,
  type SessionState,
  type Severity,
  type TextAnchor,
} from "@platform/rlhf-core"
import { RlhfSessionStoreToken, type RlhfSessionStore } from "./store"
import { RlhfDraftStoreToken, optionalDraftStore, type RlhfDraftStore } from "./drafts"
import { anchorFromSelection } from "./anchor"
import { segmentParagraph, type MarkSpec } from "./segments"
import { systemClock, type RlhfClock } from "./clock"
import { installRlhfStyles } from "./styles"
import { seamAutoGrow } from "./seam"

/** A floating annotate affordance pinned near the live selection. */
interface Pop {
  anchor: TextAnchor
  x: number
  y: number
}

interface Draft {
  anchor: TextAnchor
  kind: AnnotationKind
  severity: Severity
  gate: string
  comment: string
  rewrite: string
}

const KIND_LABEL: Record<AnnotationKind, string> = {
  issue: "Problem",
  praise: "Lob",
  question: "Rückfrage",
  "rewrite-request": "Umformulierung",
  "claim-doubt": "Beleg-Zweifel",
  "slop-flag": "Floskel",
  "evidence-gap": "Beleg-Lücke",
  "factual-correction": "Faktenkorrektur",
}
const SEVERITY_LABEL: Record<Severity, string> = {
  blocker: "Blocker",
  major: "Schwer",
  minor: "Leicht",
  nit: "Kleinigkeit",
  positive: "Positiv",
}

export class AnnotatedTextVm implements ViewModel {
  readonly draft: Signal<Draft | null> = signal<Draft | null>(null)
  readonly hint: Signal<boolean> = signal(false)
  readonly pop: Signal<Pop | null> = signal<Pop | null>(null)
  /** True once an unsaved comment/rewrite draft was restored into a re-opened form. */
  readonly draftRestored: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  private readonly storeState: ReadonlySignal<SessionState>

  constructor(
    private readonly store: RlhfSessionStore,
    readonly text: ReadonlySignal<string>,
    readonly subjectRef: ReadonlySignal<string>,
    readonly gates: ReadonlySignal<readonly string[]>,
    private readonly actor: ReadonlySignal<string>,
    private readonly clock: RlhfClock,
    private readonly drafts: RlhfDraftStore | null = null,
  ) {
    this.storeState = store.state()
    this.renderDeps = [text, subjectRef, gates, this.draft, this.hint, this.pop, this.draftRestored, this.storeState]
  }

  /** The keys the in-progress annotation comment/rewrite are buffered under (per subject). */
  private commentField(): string {
    return `annotation:${this.subjectRef.value}:comment`
  }
  private rewriteField(): string {
    return `annotation:${this.subjectRef.value}:rewrite`
  }

  /** Show the floating annotate affordance at a selection anchor + position. */
  showPop(anchor: TextAnchor, x: number, y: number): void {
    this.pop.set({ anchor, x, y })
  }

  /** Hide the floating affordance (idempotent — no churn when already hidden). */
  hidePop(): void {
    if (this.pop.value) this.pop.set(null)
  }

  /** Paragraphs of the letter (blank-line split — matches the server segmenter). */
  paragraphs(): string[] {
    return this.text.value
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0)
  }

  /** The active annotations targeting this subject (folded state, filtered). */
  subjectAnnotations(): ReadonlyArray<AnnotationOf<string>> {
    const ref = this.subjectRef.value
    return this.storeState.value.annotations.filter((a) => a.subjectRef === ref)
  }

  /** The active annotations targeting this subject, as inline-mark specs. */
  marks(): MarkSpec[] {
    return this.subjectAnnotations().map((a) => ({
      id: a.id,
      exact: a.anchor.exact,
      kind: a.kind,
      severity: a.severity,
    }))
  }

  /** Open the annotation form for a captured selection anchor. */
  begin(anchor: TextAnchor): void {
    this.hint.set(false)
    this.pop.set(null)
    // Restore an in-progress comment/rewrite for this subject if one survived a reload
    // (the anchor is fresh — the operator re-selects the span, the words come back).
    const comment = this.drafts?.read(this.commentField()) ?? ""
    const rewrite = this.drafts?.read(this.rewriteField()) ?? ""
    this.draftRestored.set(comment.length > 0 || rewrite.length > 0)
    this.draft.set({
      anchor,
      kind: "issue",
      severity: "minor",
      gate: this.gates.value[0] ?? "none",
      comment,
      rewrite,
    })
  }

  /** Show the "please select text first" hint (no selection was captured). */
  flashHint(): void {
    this.hint.set(true)
  }

  patch(field: Partial<Draft>): void {
    const cur = this.draft.value
    if (cur) this.draft.set({ ...cur, ...field })
    // Autosave the free text so a reload mid-annotation doesn't lose the words.
    if (field.comment !== undefined) this.drafts?.write(this.commentField(), field.comment)
    if (field.rewrite !== undefined) this.drafts?.write(this.rewriteField(), field.rewrite)
  }

  cancel(): void {
    // Keep the buffered draft — re-opening the form restores it; only a save clears it.
    this.draft.set(null)
    this.draftRestored.set(false)
  }

  /** Append the annotation. No-op when the comment is empty (a note needs a reason). */
  submit(): void {
    const d = this.draft.value
    if (!d || d.comment.trim().length === 0) return
    this.store.append({
      type: "annotation.Added",
      at: this.clock.now(),
      actor: this.actor.value,
      id: this.clock.newId(),
      draft: {
        subjectRef: this.subjectRef.value,
        anchor: d.anchor,
        kind: d.kind,
        severity: d.severity,
        gate: d.gate,
        comment: d.comment.trim(),
        ...(d.rewrite.trim() ? { suggestedRewrite: d.rewrite.trim() } : {}),
      },
    })
    // Saved — the annotation event supersedes the pre-save draft.
    this.drafts?.clear(this.commentField())
    this.drafts?.clear(this.rewriteField())
    this.draftRestored.set(false)
    this.draft.set(null)
  }
}

export class RlhfAnnotatedText extends KitElement<AnnotatedTextVm> {
  readonly #text = signal("")
  readonly #subjectRef = signal("")
  readonly #gates = signal<readonly string[]>([])
  readonly #actor = signal("operator")
  #clock: RlhfClock = systemClock()

  get text(): string {
    return this.#text.value
  }
  set text(v: string) {
    this.#text.set(v)
  }
  get subjectRef(): string {
    return this.#subjectRef.value
  }
  set subjectRef(v: string) {
    this.#subjectRef.set(v)
  }
  get gates(): readonly string[] {
    return this.#gates.value
  }
  set gates(v: readonly string[]) {
    this.#gates.set(v)
  }
  get actor(): string {
    return this.#actor.value
  }
  set actor(v: string) {
    this.#actor.set(v)
  }
  /** Test seam: inject a deterministic clock BEFORE connect. */
  set clock(c: RlhfClock) {
    this.#clock = c
  }

  protected createViewModel(ctx: KitContext): AnnotatedTextVm {
    return new AnnotatedTextVm(
      ctx.inject(RlhfSessionStoreToken),
      this.#text,
      this.#subjectRef,
      this.#gates,
      this.#actor,
      this.#clock,
      optionalDraftStore(ctx),
    )
  }

  #renderParagraph(vm: AnnotatedTextVm, text: string, marks: MarkSpec[]): TemplateResult {
    const segments = segmentParagraph(text, marks)
    return html`<p class="rlhf-para" lang="de">
      ${segments.map((seg) =>
        seg.mark
          ? html`<mark
              class=${classMap({ "rlhf-mark": true, [`is-${seg.mark.severity}`]: true })}
              data-ann=${seg.mark.id}
              title=${`${KIND_LABEL[seg.mark.kind]} · ${SEVERITY_LABEL[seg.mark.severity]}`}
              >${seg.text}</mark
            >`
          : seg.text,
      )}
    </p>`
  }

  #renderForm(vm: AnnotatedTextVm, d: Draft): TemplateResult {
    const gates = vm.gates.value
    return html`<form class="rlhf-form" aria-label="Annotation erfassen" @submit=${(e: Event) => e.preventDefault()}>
      <p class="rlhf-form-quote" lang="de">„${d.anchor.exact}“</p>
      ${vm.draftRestored.value
        ? html`<p class="rlhf-draft-hint" role="status" lang="de">Entwurf wiederhergestellt</p>`
        : nothing}
      <div class="rlhf-form-row">
        <label class="rlhf-field" lang="de">
          <span class="rlhf-field-label" lang="de">Art</span>
          <select @change=${(e: Event) => vm.patch({ kind: (e.target as HTMLSelectElement).value as AnnotationKind })}>
            ${ANNOTATION_KINDS.map(
              (k) => html`<option value=${k} ?selected=${k === d.kind} lang="de">${KIND_LABEL[k]}</option>`,
            )}
          </select>
        </label>
        <label class="rlhf-field" lang="de">
          <span class="rlhf-field-label" lang="de">Schwere</span>
          <select @change=${(e: Event) => vm.patch({ severity: (e.target as HTMLSelectElement).value as Severity })}>
            ${SEVERITIES.map(
              (s) => html`<option value=${s} ?selected=${s === d.severity} lang="de">${SEVERITY_LABEL[s]}</option>`,
            )}
          </select>
        </label>
        ${gates.length > 0
          ? html`<label class="rlhf-field" lang="de">
              <span class="rlhf-field-label" lang="de">Gate</span>
              <select @change=${(e: Event) => vm.patch({ gate: (e.target as HTMLSelectElement).value })}>
                ${gates.map((g) => html`<option value=${g} ?selected=${g === d.gate} lang="de">${g}</option>`)}
              </select>
            </label>`
          : nothing}
      </div>
      <label class="rlhf-field" lang="de">
        <span class="rlhf-field-label" lang="de">Kommentar</span>
        <textarea
          class="rlhf-seam rlhf-comment"
          rows="1"
          ${seamAutoGrow()}
          placeholder="Was ist hier das Problem — und warum?"
          .value=${d.comment}
          @input=${(e: Event) => vm.patch({ comment: (e.target as HTMLTextAreaElement).value })}
          lang="de"
        ></textarea>
      </label>
      <label class="rlhf-field" lang="de">
        <span class="rlhf-field-label" lang="de">Umformulierung (optional)</span>
        <textarea
          class="rlhf-seam rlhf-rewrite"
          rows="1"
          ${seamAutoGrow()}
          placeholder="Bessere Formulierung vorschlagen …"
          .value=${d.rewrite}
          @input=${(e: Event) => vm.patch({ rewrite: (e.target as HTMLTextAreaElement).value })}
          lang="de"
        ></textarea>
      </label>
      <div class="rlhf-form-actions" role="group">
        <button type="button" class="rlhf-btn rlhf-btn-primary" data-act="annotation-submit" lang="de">Speichern</button>
        <button type="button" class="rlhf-btn rlhf-btn-quiet" data-act="annotation-cancel" lang="de">Abbrechen</button>
      </div>
    </form>`
  }

  /** Capture the live selection on mouseup and pin the floating affordance to it. */
  #onTextMouseup(vm: AnnotatedTextVm, textRoot: HTMLElement): void {
    const anchor = anchorFromSelection(textRoot)
    if (!anchor) {
      vm.hidePop()
      return
    }
    const view = textRoot.ownerDocument?.defaultView
    const selection = view?.getSelection?.()
    const host = this.querySelector(".rlhf-annotated") as HTMLElement | null
    if (!selection || selection.rangeCount === 0 || !host) {
      vm.begin(anchor) // no geometry available (e.g. keyboard selection) → open directly
      return
    }
    const rect = selection.getRangeAt(0).getBoundingClientRect()
    const hostRect = host.getBoundingClientRect()
    vm.showPop(anchor, rect.left + rect.width / 2 - hostRect.left, rect.bottom - hostRect.top)
  }

  protected render(vm: AnnotatedTextVm): TemplateResult {
    const marks = vm.marks()
    const paras = vm.paragraphs()
    const draft = vm.draft.value
    const pop = vm.pop.value
    const subjectMarks = marks
    return html`
      <section class="rlhf-annotated" urn=${`urn:rlhf:subject:${vm.subjectRef.value}`}>
        <div
          class="rlhf-text"
          @mouseup=${(e: MouseEvent) => this.#onTextMouseup(vm, e.currentTarget as HTMLElement)}
        >
          ${paras.map((p) => this.#renderParagraph(vm, p, subjectMarks))}
        </div>
        ${pop
          ? html`<button
              type="button"
              class="rlhf-pop"
              data-act="annotate-pop"
              style=${styleMap({ left: `${pop.x}px`, top: `${pop.y}px` })}
              lang="de"
            >
              Annotieren
            </button>`
          : nothing}
        <div class="rlhf-affordances" role="group" aria-label="Annotieren">
          <button type="button" class="rlhf-btn rlhf-btn-quiet rlhf-affordance" data-act="annotate-selection" lang="de">
            Markierung annotieren
          </button>
          ${vm.hint.value
            ? html`<span class="rlhf-hint" role="status" lang="de">Bitte zuerst einen Textabschnitt markieren.</span>`
            : nothing}
        </div>
        ${draft ? this.#renderForm(vm, draft) : nothing}
        ${subjectMarks.length > 0
          ? html`<ul class="rlhf-gutter" aria-label="Annotationen">
              ${repeat(
                vm.subjectAnnotations(),
                (a) => a.id,
                (a) => html`<li class="rlhf-chip" data-ann=${a.id} lang="de">
                  <span class="rlhf-chip-kind">${KIND_LABEL[a.kind]}</span>
                  <span class="rlhf-chip-sev">${SEVERITY_LABEL[a.severity]}</span>
                  <span class="rlhf-chip-comment">${a.comment}</span>
                </li>`,
              )}
            </ul>`
          : nothing}
      </section>
    `
  }

  protected override actions(vm: AnnotatedTextVm): ActionMap {
    return {
      "annotate-selection": () => {
        const root = this.querySelector(".rlhf-text")
        const anchor = root ? anchorFromSelection(root) : null
        if (anchor) vm.begin(anchor)
        else vm.flashHint()
      },
      "annotate-pop": () => {
        const anchor = vm.pop.value?.anchor
        if (anchor) vm.begin(anchor)
      },
      "annotation-submit": () => vm.submit(),
      "annotation-cancel": () => vm.cancel(),
    }
  }
}
